import { useLayoutEffect, useRef } from 'react';
import { useLocation } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { moveHostIdentity, useAuth } from '@features/auth';
import { env } from '@shared/config/env';
import { api } from '@shared/api';
import { mountHelpdeskWidget } from '@widgets/helpdesk-embed';
import styles from '../styles/globals.css?inline';

/** Auth changes destroy drafts, requests, blob URLs and caches before the next actor can read. */
export function MoveHelpdeskWidget() {
  const standalone = useLocation({
    select: (location) => /^\/(en|ru)\/helpdesk\/?$/.test(location.pathname),
  });
  const { user, isLoading } = useAuth();
  const authSettled = useRef(false);
  if (!isLoading) authSettled.current = true;
  const waitingForAuth = isLoading && !authSettled.current;
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru' : 'en';
  const accountId = user?.id;
  const email = user?.email;
  useLayoutEffect(() => {
    if (waitingForAuth || standalone) return;
    const widget = mountHelpdeskWidget(
      {
        apiBase: env.VITE_API_URL,
        source: 'move_source',
        supportUrl: new URL(`/${locale}/?helpdesk=open`, window.location.origin).href,
        locale,
        initialOpen: new URLSearchParams(window.location.search).get('helpdesk') === 'open',
      },
      styles,
      undefined,
      {
        expectedAccount: accountId !== undefined,
        recoverSession: () => api.refreshSession(),
        resolveHostIdentity: moveHostIdentity(queryClient, accountId),
      }
    );
    return () => widget?.destroy();
  }, [accountId, email, waitingForAuth, locale, standalone, queryClient]);
  return null;
}
